// Device ink colours for on-screen marks: Agent ink (agent highlights,
// drawings, callouts, field cues) and My ink (the user's own strokes and the
// marked screenshot). Stored as the CSS colour itself so the Denker green
// token follows light and dark; synced live to every running webview.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { PRESET_COLORS } from "@/components/ui/color-picker";
import { syncPersistedPreferences } from "@/desktop/sync-persisted-preferences";

export const DEFAULT_INK = "var(--color-accent)";

// The project green is dropped: it is nearly the accent. 8 = the picker's 4×2 grid.
export const INK_SWATCHES = [
  DEFAULT_INK,
  ...PRESET_COLORS.filter((color) => color !== "#30D158"),
];

interface InkColors {
  agentInk: string;
  userInk: string;
}

interface InkSettings extends InkColors {
  setAgentInk: (value: string) => void;
  setUserInk: (value: string) => void;
}

function isInkSwatch(value: unknown): value is string {
  return typeof value === "string" && INK_SWATCHES.includes(value);
}

function validInkColors(value: unknown, fallback: InkColors): InkColors {
  const incoming =
    value && typeof value === "object" ? (value as Partial<InkColors>) : {};
  return {
    agentInk: isInkSwatch(incoming.agentInk)
      ? incoming.agentInk
      : fallback.agentInk,
    userInk: isInkSwatch(incoming.userInk)
      ? incoming.userInk
      : fallback.userInk,
  };
}

export const useInkSettingsStore = create<InkSettings>()(
  persist(
    (set) => ({
      agentInk: DEFAULT_INK,
      userInk: DEFAULT_INK,
      setAgentInk: (value) => {
        if (isInkSwatch(value)) set({ agentInk: value });
      },
      setUserInk: (value) => {
        if (isInkSwatch(value)) set({ userInk: value });
      },
    }),
    {
      name: "denker-ink-settings",
      version: 1,
      partialize: ({ agentInk, userInk }) => ({ agentInk, userInk }),
      merge: (persisted, current) => ({
        ...current,
        ...validInkColors(persisted, current),
      }),
    },
  ),
);

export const useAgentInk = () => useInkSettingsStore((s) => s.agentInk);
export const useUserInk = () => useInkSettingsStore((s) => s.userInk);

/** Canvas needs a literal colour, so a CSS token is read from the root. */
export function resolveInkColor(value: string): string {
  const token = /^var\((--[\w-]+)\)$/.exec(value)?.[1];
  if (!token) return value;
  return getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
}

const stopPreferenceSync = syncPersistedPreferences({
  name: "ink",
  read: () => {
    const { agentInk, userInk } = useInkSettingsStore.getState();
    return { agentInk, userInk };
  },
  subscribe: useInkSettingsStore.subscribe,
  apply: (value) => {
    if (!value || typeof value !== "object") return;
    const incoming = value as Partial<InkColors>;
    if (!isInkSwatch(incoming.agentInk) || !isInkSwatch(incoming.userInk))
      return;
    useInkSettingsStore.setState({
      agentInk: incoming.agentInk,
      userInk: incoming.userInk,
    });
  },
});
if (import.meta.hot) import.meta.hot.dispose(stopPreferenceSync);
